import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BASE_API_URL } from '../consts/base-url';
import type { UserGroup, UserGroupWithMembers } from '../types/userGroup';
import customFetch from '../utils/fetch';
export const useCreateOrUpdateGroup = (groupId: number | null, groupName: string | null, members: number[]) => {
  const queryClient = useQueryClient();
  const {
    mutate,
    data,
    isPending,
    isSuccess,
    isError,
    error
  } = useMutation({
    mutationKey: ['createOrUpdateGroup', groupId, groupName, members],
    mutationFn: async ({
      groupId,
      groupName,
      members
    }: {
      groupId: number | null;
      groupName: string | null;
      members: number[];
    }) => {
      if (groupId === null && groupName === null) {
        throw new Error('Any one of Group ID and name is required');
      }
      if (members.length === 0) {
        throw new Error('At least one member is required');
      }
      if (groupId !== null) {
        groupName = null; // send only one
      }
      const response = await customFetch(`${BASE_API_URL}/user/group`, {
        method: 'POST',
        body: JSON.stringify({
          name: groupName,
          id: groupId,
          members
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const {
        success,
        error,
        data
      } = (await response.json() as {
        success: boolean;
        error: string;
        data: UserGroup;
      });
      if (!response.ok || !success) {
        throw new Error(`Failed on user request: ${error}`);
      }
      queryClient.setQueryData(['getUserGroups'], (oldData: UserGroupWithMembers) => {
        const groups = oldData.groups.map(group => group.id === data.id ? data : group);
        if (groupId === null) {
          // this is new group, push it to the list
          groups.push(data);
        }
        return {
          users: oldData.users,
          groups
        };
      });
      return {
        data
      };
    }
  });
  return {
    createOrUpdateGroup: mutate,
    actionStatus: data,
    isPending,
    isSuccess,
    isError,
    error
  };
};